import { useTranslation } from 'react-i18next';

import { Switch } from '@/components/atoms/Switch';
import Tooltip from '@/components/atoms/Tooltip';
import { ChartHeaderActions } from '@/components/charts/actions';
import { MetricChart } from '@/components/charts/generic';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { RetryProps } from '@/components/retryFailedQueries';
import type { RespirationDataPoint } from '@/domain/behavior';
import type { ApiError } from '@/domain/shared/errors';

import { useOverviewRespirationChartData } from '../../hooks/useOverviewRespirationChartData';

interface RespirationOverviewChartProps {
    dateRange: DateRange;
    onDateChange: (data: DateRange) => void;
    respirationDataPoints: RespirationDataPoint[];
    isLoading: boolean;
    error: ApiError | null;
    /** Try again in the chart's error state. */
    retry?: RetryProps;
}

export default function RespirationOverviewChart(props: RespirationOverviewChartProps) {
    const {
        dateRange,
        isLoading = false,
        error,
        retry,
        onDateChange,
        respirationDataPoints,
    } = props;
    const { t } = useTranslation('behaviorModule');

    const { chart, showBreathingRate, setShowBreathingRate } = useOverviewRespirationChartData({
        respirationDataPoints,
    });

    const legendRight = (
        <Tooltip layout="fit" content={t('tooltips.breathingRate')}>
            <Switch
                label={t('common.respirationRate')}
                checked={showBreathingRate}
                onCheckedChange={setShowBreathingRate}
                labelPosition="left"
            />
        </Tooltip>
    );

    return (
        <ContentBlock
            title={t('respirationView.title')}
            containerRef={chart.fullScreenContainerRef}
            headerActions={
                <ChartHeaderActions
                    disabled={isLoading}
                    chartRef={chart.chartRef}
                    chartName="respiration-chart"
                    csvExport={false}
                    isFullScreen={chart.isFullScreen}
                    onFullScreenToggle={chart.handleFullScreenToggle}
                    dateFrom={dateRange.dateFrom}
                    dateTo={dateRange.dateTo}
                    onDateChange={onDateChange}
                    defaultPresetId="last7days"
                    show24hPreset={true}
                />
            }>
            <MetricChart
                legendRowLayout="centered"
                kind="line"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                legendRight={legendRight}
                chartRef={chart.chartRef}
                height={chart.isFullScreen ? 400 : 230}
                isLoading={isLoading}
                error={error}
                onRetry={retry?.onRetry}
                isRetrying={retry?.isRetrying}
            />
        </ContentBlock>
    );
}
